import { addToast, showDialogConfirm } from '@octano/global-ui';
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Card, Col, Row } from 'reactstrap';

import { useActionKitImp } from '../../../components/ozmoit/common';
import SectionTitle from '../../../components/text/SectionTitle';
import { TranslationsKeys } from '../../../locales/translations';

import {
  deleteCourseGroupAction,
  getCourseGroupParentsAction,
  getCourseGroupsListAction,
} from './actions';
import { useColumnsList } from './hooks/useColumnsList';
import CourseGroupDetailModal from './parts/CourseGroupDetailModal';
import CourseGroupTable from './parts/CourseGroupTable';
import CreateCourseGroupModal from './parts/CreateCourseGroupModal';
import SearchFilters, { SearchFiltersOutput } from './parts/SearchFilters';
import { CourseGroupListItem } from './types';
import { extractBackendErrorCode } from './utils/extractBackendErrorCode';

const ITEMS_PER_PAGE = 10;

/**
 * HU 15564 — Listado de Agrupaciones de Actividades.
 * Filtros: Escuela / Nombre / Código. Tabla paginada con acciones editar
 * (navega a la vista de items, HU 15571) y eliminar (confirm + delete con
 * branch específica para COURSE_GROUP_IN_USE).
 */
export default function CourseGroupsList(): ReactElement {
  const { t } = useTranslation(TranslationsKeys.COURSE_GROUPS);
  const history = useHistory();

  const [filters, setFilters] = useState<SearchFiltersOutput>({});
  const [page, setPage] = useState<number>(1);
  const [items, setItems] = useState<CourseGroupListItem[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [detailOpenId, setDetailOpenId] = useState<number | null>(null);
  const [refetchKey, setRefetchKey] = useState<number>(0);

  // C2 — los refetches post-mutación NO deben mostrar el spinner: mantenemos
  // las filas previas hasta que llegan las nuevas. Sólo el primer load enseña
  // el loading state.
  const initializedRef = useRef<boolean>(false);

  const isSearch = useMemo<boolean>(
    () =>
      Boolean(filters.schoolId) ||
      Boolean(filters.name) ||
      Boolean(filters.code),
    [filters],
  );

  const { action: fetchList, loading: loadingList } = useActionKitImp(
    getCourseGroupsListAction,
    {
      onSuccess: (resp) => {
        setItems(resp?.items ?? []);
        setTotal(resp?.total ?? 0);
        const itemsPerPage = resp?.items_per_page ?? ITEMS_PER_PAGE;
        setTotalPages(
          itemsPerPage > 0 ? Math.ceil((resp?.total ?? 0) / itemsPerPage) : 0,
        );
        initializedRef.current = true;
      },
      onError: {
        unknown: () => {
          addToast({
            color: 'danger',
            icon: 'error',
            text: t('errors.generic'),
          });
        },
      },
    },
  );

  useEffect(() => {
    fetchList({
      ...filters,
      page: page - 1,
      items_per_page: ITEMS_PER_PAGE,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, refetchKey]);

  const { action: deleteAction } = useActionKitImp(deleteCourseGroupAction, {
    onSuccess: () => {
      addToast({
        color: 'success',
        icon: 'success',
        text: t('list.deleteSuccess'),
      });
      setRefetchKey((prev) => prev + 1);
    },
    onError: {
      unknown: (error) => {
        // C1 — los wrappers de actions/index.ts ahora re-lanzan
        // `response.error` (shape { code, status, data, error }), donde
        // `data` es el body HTTP del backend. extractBackendErrorCode
        // navega ese path de forma type-safe.
        const code = extractBackendErrorCode(error);
        const text =
          code === 'COURSE_GROUP_IN_USE'
            ? t('errors.inUse')
            : t('errors.generic');
        addToast({ color: 'danger', icon: 'error', text });
      },
    },
  });

  const { action: fetchParents } = useActionKitImp(
    getCourseGroupParentsAction,
    {
      onError: {
        unknown: () => {
          addToast({
            color: 'danger',
            icon: 'error',
            text: t('errors.generic'),
          });
        },
      },
    },
  );

  const handleApplyFilters = useCallback((next: SearchFiltersOutput) => {
    setFilters(next);
    setPage(1);
  }, []);

  // M6 — callbacks individuales memoizados para que el `useMemo` interno de
  // useColumnsList no se invalide en cada render por una identidad nueva del
  // objeto de callbacks.
  const handleTooltipDetail = useCallback(
    (id: number) => setDetailOpenId(id),
    [],
  );

  const handleEditNavigate = useCallback(
    (id: number) => {
      history.push(`/academic-offers/course-groups/${id}/items`);
    },
    [history],
  );

  const handleDelete = useCallback(
    async (group: CourseGroupListItem) => {
      // Antes de mostrar el diálogo de confirmación, consultamos si la
      // agrupación está asignada como sub-agrupación de otra. Si lo está,
      // mostramos un mensaje alternativo que advierte que también será
      // removida de las agrupaciones que la contienen.
      const result = await fetchParents(group.id);
      if (!result || !result.success) {
        return;
      }
      const isAssignedAsChild = (result.data.parents?.length ?? 0) > 0;

      const dialogKey = isAssignedAsChild
        ? 'deleteConfirmAsChild'
        : 'deleteConfirm';

      showDialogConfirm({
        icon: { name: 'warning', color: 'warning' },
        title: t(`${dialogKey}.title`),
        subtitle: t(`${dialogKey}.subtitle`, { name: group.name }),
        btnConfirm: {
          text: t(`${dialogKey}.btnConfirm`),
          onConfirm: () => {
            deleteAction(group.id);
          },
        },
        btnClose: { text: t(`${dialogKey}.btnClose`) },
      });
    },
    [t, deleteAction, fetchParents],
  );

  const columns = useColumnsList(
    handleTooltipDetail,
    handleEditNavigate,
    handleDelete,
  );

  return (
    <Card className="p-4 mt-2 mx-3">
      <SectionTitle text={t('module.title')} />
      <p className="fs-16 text-light mt-3 mb-3">{t('module.description')}</p>

      <Row>
        <Col xs={12}>
          <SearchFilters
            onApply={handleApplyFilters}
            canCreate
            onCreate={() => setCreateOpen(true)}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={12}>
          <CourseGroupTable
            items={items}
            total={total}
            totalPages={totalPages}
            page={page}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setPage}
            columns={columns}
            loading={loadingList && !initializedRef.current}
            isSearch={isSearch}
          />
        </Col>
      </Row>

      <CreateCourseGroupModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={() => {
          setCreateOpen(false);
          setRefetchKey((prev) => prev + 1);
        }}
      />

      <CourseGroupDetailModal
        isOpen={detailOpenId !== null}
        groupId={detailOpenId ?? 0}
        onClose={() => setDetailOpenId(null)}
      />
    </Card>
  );
}
